import React, { useMemo, useState } from 'react';
import theme from './UnitSelector.module.scss';
import { getResidentialUnitsByProjectId } from '@/services/residential-units';
import { useQuery } from '@tanstack/react-query';
import { uniq } from 'lodash-es';
import { ProjectTagType, ResidentialUnitExtraData } from '@/types';
import { Accordion, Checkbox, CheckboxDropdown, Section } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { checkSubsetSelection, toggleArrayValues } from '@/utils/arrays';
import { GroupedResidentialUnits, groupResidentialUnits } from '@/utils/residential-units';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { t } from '@lingui/core/macro';
import { getProjectTags } from '@/services/project-tags';

export interface UnitSelectorProps {
    selectedResidentialUnitIds: number[];
    onChange(newValue: number[]): void;
    projectId: number;
    filter?(unit: ResidentialUnitExtraData): boolean;
    /**
     * Units that must stay selected and cannot be toggled off (e.g. they have a live order). Enforced at the
     * checkbox is disabled and they keep whatever selection state they were given. The link editor passes
     * them as selected (so they can't be unlinked); the unpublish dialog passes them unselected (so they
     * can't be picked for unpublish).
     */
    lockedResidentialUnitIds?: number[];
}

export const UnitSelector: React.FC<UnitSelectorProps> = ({
    projectId,
    selectedResidentialUnitIds,
    onChange,
    filter,
    lockedResidentialUnitIds,
}) => {
    const lockedUnitIds = useMemo(() => new Set(lockedResidentialUnitIds ?? []), [lockedResidentialUnitIds]);

    // Emit a new selection while leaving locked units at their current state: bulk-select, group-select and
    // filter actions never add or remove them (the individual checkbox is disabled separately).
    const emitChange = (unitIds: number[]) => {
        if (lockedUnitIds.size === 0) {
            onChange(unitIds);
            return;
        }
        const withoutLocked = unitIds.filter((id) => !lockedUnitIds.has(id));
        const currentlySelectedLocked = selectedResidentialUnitIds.filter((id) => lockedUnitIds.has(id));
        onChange(uniq([...withoutLocked, ...currentlySelectedLocked]));
    };

    const { data: units } = useQuery({
        queryKey: ['units', projectId],
        queryFn: () => getResidentialUnitsByProjectId(projectId),
    });

    const { data: tags } = useQuery({
        queryKey: ['tags', projectId],
        queryFn: () => getProjectTags(projectId, ProjectTagType.Room),
    });

    const filteredUnits = useMemo(() => {
        if (!units) {
            return null;
        }

        let relevantUnits = units;
        if (filter) {
            relevantUnits = units.filter(filter);
        }

        return relevantUnits;
    }, [units, filter]);

    const groupedUnits = useMemo(() => {
        if (!filteredUnits) {
            return null;
        }

        return groupResidentialUnits(filteredUnits, (x) => x);
    }, [filteredUnits]);

    const handleChange = (unitIds: number[], flag: boolean) => {
        emitChange(toggleArrayValues(selectedResidentialUnitIds, unitIds, flag));
    };

    // Filter criteria are independent of the selection: picking values in the
    // dropdowns AND-narrows all units and *sets* the selection (legacy behavior).
    const [unitFilter, setUnitFilter] = useState<{ floors: string[]; layoutTypes: string[]; tags: number[] }>({
        floors: [],
        layoutTypes: [],
        tags: [],
    });

    const unitsByTag = useMemo(() => {
        if (!filteredUnits || !tags) {
            return null;
        }

        const result = new Map<number, Set<number>>(tags.map((x) => [x.id, new Set()]));
        filteredUnits.forEach((u) => {
            u.projectTags.forEach((x) => {
                result.get(x.id)?.add(u.id);
            });
        });

        return result;
    }, [filteredUnits, tags]);

    const floorOptions = useMemo(() => (filteredUnits ? uniq(filteredUnits.map((x) => x.floor)) : []), [filteredUnits]);
    const layoutTypeOptions = useMemo(
        () => (filteredUnits ? uniq(filteredUnits.map((x) => x.layoutType)) : []),
        [filteredUnits]
    );
    const tagOptions = useMemo(() => {
        if (!unitsByTag || !tags) {
            return [];
        }
        return tags.filter((x) => (unitsByTag.get(x.id)?.size ?? 0) > 0);
    }, [unitsByTag, tags]);

    const applyFilter = (f: { floors: string[]; layoutTypes: string[]; tags: number[] }) => {
        if (!filteredUnits) {
            return;
        }

        if (f.floors.length === 0 && f.layoutTypes.length === 0 && f.tags.length === 0) {
            emitChange([]);
            return;
        }

        let result = filteredUnits;
        if (f.floors.length > 0) {
            result = result.filter((u) => f.floors.includes(u.floor));
        }
        if (f.layoutTypes.length > 0) {
            result = result.filter((u) => f.layoutTypes.includes(u.layoutType));
        }
        if (f.tags.length > 0) {
            result = result.filter((u) => u.projectTags.some((t) => t.id != null && f.tags.includes(t.id)));
        }

        emitChange(result.map((u) => u.id));
    };

    const handleFloorSelect = (floors: string[], checked: boolean) => {
        const next = { ...unitFilter, floors: toggleArrayValues(unitFilter.floors, floors, checked) };
        setUnitFilter(next);
        applyFilter(next);
    };

    const handleLayoutTypeSelect = (layoutTypes: string[], checked: boolean) => {
        const next = { ...unitFilter, layoutTypes: toggleArrayValues(unitFilter.layoutTypes, layoutTypes, checked) };
        setUnitFilter(next);
        applyFilter(next);
    };

    const handleTagSelect = (tags: number[], checked: boolean) => {
        const next = { ...unitFilter, tags: toggleArrayValues(unitFilter.tags, tags, checked) };
        setUnitFilter(next);
        applyFilter(next);
    };

    if (!filteredUnits || !unitsByTag || !groupedUnits) {
        return <LoadingIndicator />;
    }

    // Locked units can't be toggled, so "select all" reflects and operates on the selectable ones only -
    // otherwise a group with a locked unit could never reach the 'all' state.
    const selectableUnitIds = units!.filter((x) => !lockedUnitIds.has(x.id)).map((x) => x.id);
    const allSelectionState = checkSubsetSelection(selectedResidentialUnitIds, selectableUnitIds);

    return (
        <div>
            <Section variant="highlight">
                <div className={theme.bulkSelect}>
                    <div>
                        <span>
                            <Trans>Floor</Trans>
                        </span>
                        <CheckboxDropdown
                            className={{
                                button: theme.bulkDropdown,
                            }}
                            checkedValues={unitFilter.floors}
                            indeterminateValues={[]}
                            data={floorOptions}
                            display={(x) => x || t`(empty)`}
                            value={(x) => x}
                            onChange={handleFloorSelect}
                        />
                    </div>

                    <div>
                        <span>
                            <Trans>Tags</Trans>
                        </span>
                        <CheckboxDropdown
                            className={{
                                button: theme.bulkDropdown,
                            }}
                            checkedValues={unitFilter.tags}
                            indeterminateValues={[]}
                            data={tagOptions}
                            display={(x) => x.label || t`(empty)`}
                            value={(x) => x.id}
                            onChange={handleTagSelect}
                        />
                    </div>

                    <div>
                        <span>
                            <Trans>Layout type</Trans>
                        </span>
                        <CheckboxDropdown
                            className={{
                                button: theme.bulkDropdown,
                            }}
                            checkedValues={unitFilter.layoutTypes}
                            indeterminateValues={[]}
                            data={layoutTypeOptions}
                            display={(x) => x || t`(empty)`}
                            value={(x) => x}
                            onChange={handleLayoutTypeSelect}
                        />
                    </div>
                </div>
                <Checkbox
                    checked={allSelectionState === 'all'}
                    indeterminate={allSelectionState === 'some'}
                    onChange={(e) => handleChange(selectableUnitIds, e.target.checked)}>
                    <Trans>Select all</Trans>
                </Checkbox>
            </Section>

            <div>
                {groupedUnits.map((x) => (
                    <GroupAccordion
                        key={x.id}
                        group={x}
                        selectedResidentialUnitIds={selectedResidentialUnitIds}
                        lockedUnitIds={lockedUnitIds}
                        onChange={handleChange}
                    />
                ))}
            </div>
        </div>
    );
};

interface GroupAccordionProps {
    group: GroupedResidentialUnits;
    selectedResidentialUnitIds: number[];
    lockedUnitIds: Set<number>;
    onChange(unitIds: number[], flag: boolean): void;
}

const GroupAccordion: React.FC<GroupAccordionProps> = ({ selectedResidentialUnitIds, lockedUnitIds, onChange, group }) => {
    // Locked units can't be toggled, so the group checkbox reflects/operates on the selectable ones only -
    // otherwise a group containing a locked unit could never reach 'all' and the toggle breaks.
    const selectableUnitIds = group.units.filter((x) => !lockedUnitIds.has(x.id)).map((x) => x.id);
    const groupSelectionState = checkSubsetSelection(selectedResidentialUnitIds, selectableUnitIds);

    return (
        <Accordion
            variant="bordered"
            defaultOpen={true}
            title={
                <>
                    {group.id === null ? <Trans>Unassigned</Trans> : group.name}
                    <Checkbox
                        className={theme.groupCheckbox}
                        checked={groupSelectionState === 'all'}
                        indeterminate={groupSelectionState === 'some'}
                        onChange={(e) => onChange(selectableUnitIds, e.target.checked)}>
                        <Trans>Select whole group</Trans>
                    </Checkbox>
                </>
            }>
            <ul className={theme.unitList}>
                {group.units.map((x) => {
                    const locked = lockedUnitIds.has(x.id);
                    return (
                        <li key={x.id}>
                            <Checkbox
                                checked={selectedResidentialUnitIds.includes(x.id)}
                                disabled={locked}
                                onChange={(e) => onChange([x.id], e.target.checked)}>
                                {x.name}
                                {locked && (
                                    <span className={theme.lockedHint}>
                                        <Trans>Ordered</Trans>
                                    </span>
                                )}
                            </Checkbox>
                        </li>
                    );
                })}
            </ul>
        </Accordion>
    );
};
